<div class="custom-reports-container">
  <!-- Header Toolbar -->
  <p-toolbar>
    <div class="p-toolbar-group-start">
      <h2 class="m-0">Advanced Custom Reports</h2>
    </div>
    <div class="p-toolbar-group-end">
      <p-button appGlobalLoadingDisable 
        label="Use Template" 
        icon="pi pi-file-o" 
        (onClick)="showTemplates()"
        severity="secondary"
        class="mr-2">
      </p-button>
      <p-button appGlobalLoadingDisable 
        label="Create Advanced Report" 
        icon="pi pi-plus" 
        (onClick)="createNewReport()"
        severity="primary">
      </p-button>
    </div>
  </p-toolbar>

  <!-- Reports List -->
  <p-card class="mt-3">
    <div class="flex justify-between items-center mb-4">
      <h3>My Reports</h3>
      <p-iconField iconPosition="left">
        <p-inputIcon class="pi pi-search"></p-inputIcon>
        <input 
          pInputText 
          placeholder="Search reports..." i18n-placeholder
          class="w-20rem">
      </p-iconField>
    </div>

    <p-table 
      [value]="reports" 
      [loading]="loading"
      dataKey="_id"
      responsiveLayout="scroll">
      
      <ng-template pTemplate="header">
        <tr>
          <th pSortableColumn="name">
            Name 
            <p-sortIcon field="name"></p-sortIcon>
          </th>
          <th>Description</th>
          <th>Fields</th>
          <th>Features</th>
          <th pSortableColumn="lastExecuted">
            Last Executed 
            <p-sortIcon field="lastExecuted"></p-sortIcon>
          </th>
          <th>Executions</th>
          <th pSortableColumn="createdAt">
            Created 
            <p-sortIcon field="createdAt"></p-sortIcon>
          </th>
          <th>Actions</th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-report>
        <tr>
          <td>
            <div class="flex align-items-center gap-2">
              <i [class]="getReportStatusIcon(report)" 
                 [style.color]="getReportStatusColor(report) === 'success' ? '#10b981' : '#f59e0b'">
              </i>
              <span class="font-medium">{{ report.name }}</span>
              <p-tag 
                *ngIf="report.isPublic" 
                value="Public" 
                severity="info" 
                class="text-xs">
              </p-tag>
            </div>
          </td>
          <td>
            <span class="text-sm text-color-secondary">
              {{ report.description || 'No description' }}
            </span>
          </td>
          <td>
            <p-tag 
              [value]="report.selectedFields.length + ' fields'" 
              severity="secondary">
            </p-tag>
          </td>
          <td>
            <div class="flex gap-1">
              <p-tag 
                *ngIf="report.calculatedFields?.length" 
                [value]="'Calc: ' + report.calculatedFields.length" 
                severity="info" 
                class="text-xs">
              </p-tag>
              <p-tag 
                *ngIf="report.groupBy?.fields?.length" 
                value="Grouped" 
                severity="warning" 
                class="text-xs">
              </p-tag>
              <p-tag 
                *ngIf="report.chartConfig?.enabled" 
                value="Chart" 
                severity="success" 
                class="text-xs">
              </p-tag>
            </div>
          </td>
          <td>{{ formatDate(report.lastExecuted!) }}</td>
          <td>{{ report.executionCount || 0 }}</td>
          <td>{{ formatDate(report.createdAt!) }}</td>
          <td>
            <div class="flex gap-2">
              <p-button appGlobalLoadingDisable 
                icon="pi pi-play" 
                size="small" 
                severity="success"
                (onClick)="executeReport(report)"
                pTooltip="Execute Report">
              </p-button>
              <p-button appGlobalLoadingDisable 
                icon="pi pi-pencil" 
                size="small" 
                severity="info"
                (onClick)="editReport(report)"
                pTooltip="Edit Report">
              </p-button>
              <p-splitButton 
                icon="pi pi-download" 
                size="small"
                [model]="exportActionItems"
                (onClick)="currentReport = report"
                pTooltip="Export Report">
              </p-splitButton>
              <p-button appGlobalLoadingDisable 
                icon="pi pi-copy" 
                size="small" 
                severity="secondary"
                (onClick)="duplicateReport(report)"
                pTooltip="Duplicate Report">
              </p-button>
              <p-button appGlobalLoadingDisable 
                icon="pi pi-trash" 
                size="small" 
                severity="danger"
                (onClick)="deleteReport(report)"
                pTooltip="Delete Report">
              </p-button>
            </div>
          </td>
        </tr>
      </ng-template>

      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="8" class="text-center py-6">
            <div class="flex flex-column align-items-center gap-3">
              <i class="pi pi-chart-bar text-4xl text-color-secondary"></i>
              <p class="text-color-secondary">No reports found. Create your first advanced custom report!</p>
              <p-button appGlobalLoadingDisable 
                label="Create Report" 
                icon="pi pi-plus"
                (onClick)="createNewReport()">
              </p-button>
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </p-card>

  <!-- Advanced Report Builder Drawer -->
  <p-drawer 
    [(visible)]="reportBuilderVisible" 
    position="right" 
    [style]="{width: '85vw'}"
    header="Advanced Report Builder"
    (onHide)="closeDialogs()">
    
    <!-- Validation Feedback Card -->
    <p-card *ngIf="validationFeedbackVisible" class="mb-3 border-red-200">
      <div class="flex justify-between align-items-start">
        <div>
          <div class="flex align-items-center gap-2 mb-2">
            <i class="pi pi-exclamation-triangle text-red-500"></i>
            <h4 class="m-0">{{ validationFeedback.context }}</h4>
          </div>
          <p class="text-sm text-color-secondary m-0">{{ validationFeedback.message }}</p>
        </div>
        <p-button appGlobalLoadingDisable 
          icon="pi pi-times" 
          severity="danger" 
          [text]="true"
          (onClick)="dismissValidationFeedback()">
        </p-button>
      </div>
      
      <div class="mt-3" *ngIf="validationFeedback.errors?.length">
        <h5 class="m-0 mb-2 text-red-600">Errors</h5>
        <ul class="m-0 pl-3">
          <li *ngFor="let e of validationFeedback.errors" class="text-sm text-red-600">{{ e }}</li>
        </ul>
      </div>
      
      <div class="mt-3" *ngIf="validationFeedback.warnings?.length">
        <h5 class="m-0 mb-2 text-yellow-700">Warnings</h5>
        <ul class="m-0 pl-3">
          <li *ngFor="let w of validationFeedback.warnings" class="text-sm text-yellow-700">{{ w }}</li>
        </ul>
      </div>
    </p-card>
    
    <!-- Builder Steps -->
    <div class="builder-header p-3 border-bottom-1 ">
      <p-steps 
        [model]="builderSteps" 
        [activeIndex]="builderStep"
        [readonly]="false"
        (activeIndexChange)="goToStep($event)">
      </p-steps>
    </div>
    
    <div class="builder-content p-4">
      <form [formGroup]="reportForm">
        <!-- Step Navigation -->
        <div class="step-navigation mb-4">
          <div class="flex justify-between align-items-center">
            <h3 class="m-0">{{ builderSteps[builderStep].label }}</h3>
            <div class="flex gap-2">
              <p-button 
                *ngIf="builderStep > 0"
                label="Previous" 
                icon="pi pi-chevron-left"
                severity="secondary"
                size="small"
                (onClick)="prevStep()">
              </p-button>
              <p-button 
                *ngIf="builderStep < builderSteps.length - 1"
                label="Next" 
                icon="pi pi-chevron-right"
                iconPos="right"
                size="small"
                [disabled]="!canProceedToNextStep()"
                (onClick)="nextStep()">
              </p-button>
            </div>
          </div>
          <p *ngIf="getStepValidationMessage(builderStep)" class="text-sm text-red-500 mt-2">
            {{ getStepValidationMessage(builderStep) }}
          </p>
        </div>

        <!-- Step 0: Field Selection -->
        <div *ngIf="builderStep === 0">
          <p-panel header="Field Selection" class="mb-4">
            <div class="grid">
              <!-- Field Browser -->
              <div class="col-12 lg:col-6">
                <h4>Available Fields</h4>
                <div class="flex gap-2 mb-3">
                  <p-iconField iconPosition="left" class="flex-1">
                    <p-inputIcon class="pi pi-search"></p-inputIcon>
                    <input 
                      pInputText 
                      [(ngModel)]="fieldSearchText"
                      [ngModelOptions]="{standalone: true}"
                      placeholder="Search fields..." i18n-placeholder
                      class="w-full">
                  </p-iconField>
                  <p-select 
                    [(ngModel)]="selectedCategory"
                    [ngModelOptions]="{standalone: true}"
                    [options]="getCategoryOptions()"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Category" i18n-placeholder>
                  </p-select>
                </div>

                <div class="field-list max-h-20rem overflow-auto">
                  <div 
                    *ngFor="let field of filteredFields" 
                    class="p-3 border-1 border-round mb-2 cursor-pointer hover:bg-primary-50"
                    [class.bg-primary-100]="isFieldSelected(field)"
                    (click)="toggleFieldSelection(field)">
                    <div class="flex justify-between align-items-start">
                      <div class="flex-1">
                        <div class="flex align-items-center gap-2 mb-1">
                          <i [class]="getFieldTypeIcon(field.type)" class="text-sm"></i>
                          <span class="font-medium">{{ field.label }}</span>
                          <p-tag [value]="field.category" severity="secondary" class="text-xs"></p-tag>
                        </div>
                        <p class="text-sm text-color-secondary m-0">
                          {{ field.description || 'No description' }}
                        </p>
                      </div>
                      <p-checkbox 
                        [ngModel]="isFieldSelected(field)"
                        [ngModelOptions]="{standalone: true}"
                        [binary]="true"
                        (ngModelChange)="toggleFieldSelection(field)">
                      </p-checkbox>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Selected Fields -->
              <div class="col-12 lg:col-6">
                <h4>Selected Fields ({{ selectedFields.length }})</h4>
                <div class="selected-fields-list max-h-20rem overflow-auto">
                  <div 
                    *ngFor="let field of selectedFields; trackBy: trackByFieldName" 
                    class="selected-field-item p-3 border-1 border-round mb-2 bg-green-50">
                    <div class="flex justify-between align-items-center">
                      <div>
                        <div class="flex align-items-center gap-2 mb-1">
                          <i [class]="getFieldTypeIcon(field.type)" class="text-sm"></i>
                          <span class="font-medium">{{ field.label }}</span>
                        </div>
                        <p class="text-sm text-color-secondary m-0">{{ field.category }}</p>
                      </div>
                      <p-button appGlobalLoadingDisable 
                        icon="pi pi-times" 
                        size="small" 
                        severity="danger"
                        [text]="true"
                        (onClick)="removeSelectedField(field)">
                      </p-button>
                    </div>
                  </div>
                  
                  <div *ngIf="selectedFields.length === 0" class="text-center py-4 text-color-secondary">
                    No fields selected. Choose fields from the left panel.
                  </div>
                </div>
              </div>
            </div>
          </p-panel>
        </div>
        
        <!-- Step 1: Calculated Fields -->
        <div *ngIf="builderStep === 1">
          <p-panel header="Calculated Fields" class="mb-4">
            <div class="flex justify-between align-items-center mb-3">
              <h4 class="m-0">Calculated Fields ({{ calculatedFields.length }})</h4>
              <p-button appGlobalLoadingDisable 
                label="Add Calculated Field" 
                icon="pi pi-plus" 
                size="small"
                (onClick)="addCalculatedField()">
              </p-button>
            </div>

             <div *ngIf="calculatedFields.length > 0">
               <div 
                 *ngFor="let calc of calculatedFields; let i = index" 
                 class="calculated-field-item border-1  border-round mb-3">
                 <div class="field-header p-3 bg-surface-50 border-bottom-1  cursor-pointer"
                      (click)="calc.expanded = !calc.expanded">
                   <div class="flex justify-between align-items-center">
                     <h5 class="m-0">{{ calc.label || 'Calculated Field ' + (i + 1) }}</h5>
                     <i [class]="calc.expanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"></i>
                   </div>
                 </div>
                 <div *ngIf="calc.expanded" class="field-content p-3">
                <div class="grid">
                  <div class="col-12 md:col-6">
                    <label class="block text-sm font-medium mb-2">Field Name</label>
                    <input 
                      pInputText 
                      [(ngModel)]="calc.name"
                      [ngModelOptions]="{standalone: true}"
                      placeholder="field_name" i18n-placeholder
                      class="w-full">
                  </div>
                  <div class="col-12 md:col-6">
                    <label class="block text-sm font-medium mb-2">Display Label</label>
                    <input 
                      pInputText 
                      [(ngModel)]="calc.label"
                      [ngModelOptions]="{standalone: true}"
                      placeholder="Field Label" i18n-placeholder
                      class="w-full">
                  </div>
                  <div class="col-12 md:col-4">
                    <label class="block text-sm font-medium mb-2">Calculation Type</label>
                    <p-select 
                      [(ngModel)]="calc.type"
                      [ngModelOptions]="{standalone: true}"
                      [options]="calculationTypeOptions"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Select type" i18n-placeholder
                      class="w-full">
                    </p-select>
                  </div>
                  <div class="col-12 md:col-4">
                    <label class="block text-sm font-medium mb-2">Return Type</label>
                    <p-select 
                      [(ngModel)]="calc.returnType"
                      [ngModelOptions]="{standalone: true}"
                      [options]="[{label: 'Number', value: 'number'}, {label: 'Text', value: 'string'}, {label: 'Date', value: 'date'}, {label: 'Boolean', value: 'boolean'}]"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Return type" i18n-placeholder
                      class="w-full">
                    </p-select>
                  </div>
                  <div class="col-12 md:col-4">
                    <div class="flex align-items-end h-full">
                      <p-button appGlobalLoadingDisable 
                        icon="pi pi-trash" 
                        severity="danger" 
                        size="small"
                        [text]="true"
                        (onClick)="removeCalculatedField(i)"
                        pTooltip="Remove calculated field">
                      </p-button>
                    </div>
                  </div>
                  <div class="col-12">
                    <label class="block text-sm font-medium mb-2">Formula</label>
                    <textarea 
                      pTextarea 
                      [(ngModel)]="calc.formula"
                      [ngModelOptions]="{standalone: true}"
                      placeholder="Enter formula (e.g., field1 + field2, SUM(field1), etc.)" i18n-placeholder
                      rows="3"
                      class="w-full">
                    </textarea>
                     <small class="text-color-secondary block mt-1">
                       Available fields: {{ getAvailableFieldNames() }}
                     </small>
                  </div>
                  <div class="col-12">
                    <label class="block text-sm font-medium mb-2">Description</label>
                    <input 
                      pInputText 
                      [(ngModel)]="calc.description"
                      [ngModelOptions]="{standalone: true}"
                      placeholder="Describe what this calculation does" i18n-placeholder
                      class="w-full">
                  </div>
                 </div>
                 </div>
               </div>
             </div>
            
            <div *ngIf="calculatedFields.length === 0" class="text-center py-4 text-color-secondary">
              <i class="pi pi-calculator text-4xl mb-3 block"></i>
              <p>No calculated fields defined. Create calculated fields to perform complex operations on your data.</p>
            </div>
          </p-panel>
        </div>
        
        <!-- Step 2: Group By Configuration -->
        <div *ngIf="builderStep === 2">
          <p-panel header="Group By & Aggregations" class="mb-4">
            <div class="mb-4">
              <div class="flex align-items-center gap-2 mb-3">
                <p-checkbox 
                  [(ngModel)]="reportForm.controls['enableGrouping'].value"
                  [ngModelOptions]="{standalone: true}"
                  (ngModelChange)="reportForm.controls['enableGrouping'].setValue($event)"
                  inputId="enableGrouping" 
                  [binary]="true">
                </p-checkbox>
                <label for="enableGrouping" class="font-medium">Enable Grouping & Aggregations</label>
              </div>
            </div>
            
            <div *ngIf="reportForm.controls['enableGrouping'].value">
              <!-- Group By Fields -->
              <div class="mb-4">
                <h4>Group By Fields</h4>
                <p-multiSelect 
                  [ngModel]="groupByConfig?.fields || []"
                  (ngModelChange)="initializeGroupBy(); groupByConfig!.fields = $event"
                  [ngModelOptions]="{standalone: true}"
                  [options]="getGroupableFields()"
                  optionLabel="label"
                  optionValue="name"
                  placeholder="Select fields to group by" i18n-placeholder
                  class="w-full">
                </p-multiSelect>
              </div>
              
              <!-- Aggregations -->
              <div class="mb-4">
                <div class="flex justify-between align-items-center mb-3">
                  <h4 class="m-0">Aggregations</h4>
                  <p-button appGlobalLoadingDisable 
                    label="Add Aggregation" 
                    icon="pi pi-plus" 
                    size="small"
                    (onClick)="addAggregation()">
                  </p-button>
                </div>
                
                <div *ngFor="let agg of groupByConfig?.aggregations || []; let i = index" 
                     class="aggregation-item p-3 border-1  border-round mb-3">
                  <div class="grid align-items-end">
                    <div class="col-12 md:col-3">
                      <label class="block text-sm font-medium mb-2">Field</label>
                      <p-select 
                        [(ngModel)]="agg.field"
                        [ngModelOptions]="{standalone: true}"
                        [options]="getNumericFields()"
                        optionLabel="label"
                        optionValue="name"
                        placeholder="Select field" i18n-placeholder
                        class="w-full">
                      </p-select>
                    </div>
                    <div class="col-12 md:col-3">
                      <label class="block text-sm font-medium mb-2">Function</label>
                      <p-select 
                        [(ngModel)]="agg.function"
                        [ngModelOptions]="{standalone: true}"
                        [options]="aggregationOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select function" i18n-placeholder
                        class="w-full">
                      </p-select>
                    </div>
                    <div class="col-12 md:col-4">
                      <label class="block text-sm font-medium mb-2">Alias</label>
                      <input 
                        pInputText 
                        [(ngModel)]="agg.alias"
                        [ngModelOptions]="{standalone: true}"
                        placeholder="Result column name" i18n-placeholder
                        class="w-full">
                    </div>
                    <div class="col-12 md:col-2">
                      <p-button appGlobalLoadingDisable 
                        icon="pi pi-trash" 
                        severity="danger" 
                        size="small"
                        [text]="true"
                        (onClick)="removeAggregation(i)"
                        pTooltip="Remove aggregation">
                      </p-button>
                    </div>
                  </div>
                </div>
                
                <div *ngIf="!groupByConfig?.aggregations?.length" class="text-center py-4 text-color-secondary">
                  No aggregations defined. Add aggregations to calculate totals, averages, etc.
                </div>
              </div>
            </div>
            
            <div *ngIf="!reportForm.controls['enableGrouping'].value" class="text-center py-4 text-color-secondary">
              <i class="pi pi-objects-column text-4xl mb-3 block"></i>
              <p>Enable grouping to aggregate your data and create summary reports.</p>
            </div>
          </p-panel>
        </div>
        
        <!-- Step 3: Filters & Conditions -->
        <div *ngIf="builderStep === 3">
          <p-panel header="Filters & Conditions" class="mb-4">
            <div class="flex justify-between align-items-center mb-3">
              <h4 class="m-0">Conditions ({{ conditions.length }})</h4>
              <p-button appGlobalLoadingDisable 
                label="Add Condition" 
                icon="pi pi-plus" 
                size="small"
                (onClick)="addCondition()">
              </p-button>
            </div>

            <div *ngFor="let condition of conditions; let i = index" 
                 class="condition-item p-3 border-1  border-round mb-3">
              <div class="grid align-items-end">
                <div class="col-12 md:col-3">
                  <label class="block text-sm font-medium mb-2">Field</label>
                  <p-select 
                    [(ngModel)]="condition.field"
                    [ngModelOptions]="{standalone: true}"
                    [options]="selectedFields"
                    optionLabel="label"
                    optionValue="name"
                    placeholder="Select field" i18n-placeholder
                    class="w-full">
                  </p-select>
                </div>
                <div class="col-12 md:col-3">
                  <label class="block text-sm font-medium mb-2">Operator</label>
                  <p-select 
                    [(ngModel)]="condition.operator"
                    [ngModelOptions]="{standalone: true}"
                    [options]="getOperatorsForField(getFieldForCondition(condition.field)!)"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select operator" i18n-placeholder
                    class="w-full">
                  </p-select>
                </div>
                <div class="col-12 md:col-4" *ngIf="!needsNoValue(condition.operator)">
                  <label class="block text-sm font-medium mb-2">Value</label>
                  <!-- String/Text input -->
                  <input 
                    *ngIf="getFieldForCondition(condition.field)?.type === 'string' && !needsListValue(condition.operator)"
                    pInputText 
                    [(ngModel)]="condition.value" 
                    [ngModelOptions]="{standalone: true}"
                    placeholder="Enter value" i18n-placeholder
                    class="w-full">
                  
                  <!-- Number input -->
                  <p-inputNumber 
                    *ngIf="getFieldForCondition(condition.field)?.type === 'number' && !needsListValue(condition.operator)"
                    [(ngModel)]="condition.value" 
                    [ngModelOptions]="{standalone: true}"
                    placeholder="Enter number" i18n-placeholder
                    class="w-full">
                  </p-inputNumber>
                  
                  <!-- Date input -->
                  <p-datePicker 
                    *ngIf="getFieldForCondition(condition.field)?.type === 'date' && !needsListValue(condition.operator)"
                    [(ngModel)]="condition.value" 
                    [ngModelOptions]="{standalone: true}"
                    placeholder="Select date" i18n-placeholder
                    class="w-full">
                  </p-datePicker>
                  
                  <!-- Boolean input -->
                  <p-select 
                    *ngIf="getFieldForCondition(condition.field)?.type === 'boolean'"
                    [(ngModel)]="condition.value"
                    [ngModelOptions]="{standalone: true}"
                    [options]="[{label: 'True', value: true}, {label: 'False', value: false}]"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select value" i18n-placeholder
                    class="w-full">
                  </p-select>
                  
                  <!-- List input -->
                  <input 
                    *ngIf="needsListValue(condition.operator)"
                    pInputText 
                    [(ngModel)]="condition.value" 
                    [ngModelOptions]="{standalone: true}"
                    placeholder="Enter comma-separated values" i18n-placeholder
                    class="w-full">
                </div>
                <div class="col-12 md:col-4" *ngIf="needsSecondValue(condition.operator)">
                  <label class="block text-sm font-medium mb-2">To Value</label>
                  <!-- Second value inputs similar to above -->
                  <input 
                    *ngIf="getFieldForCondition(condition.field)?.type === 'string'"
                    pInputText 
                    [(ngModel)]="condition.secondValue" 
                    [ngModelOptions]="{standalone: true}"
                    placeholder="Enter second value" i18n-placeholder
                    class="w-full">
                  
                  <p-inputNumber 
                    *ngIf="getFieldForCondition(condition.field)?.type === 'number'"
                    [(ngModel)]="condition.secondValue" 
                    [ngModelOptions]="{standalone: true}"
                    placeholder="Enter second number" i18n-placeholder
                    class="w-full">
                  </p-inputNumber>
                  
                  <p-datePicker 
                    *ngIf="getFieldForCondition(condition.field)?.type === 'date'"
                    [(ngModel)]="condition.secondValue" 
                    [ngModelOptions]="{standalone: true}"
                    placeholder="Select end date" i18n-placeholder
                    class="w-full">
                  </p-datePicker>
                </div>
                <div class="col-12 md:col-2">
                  <p-button appGlobalLoadingDisable 
                    icon="pi pi-trash" 
                    severity="danger" 
                    size="small"
                    [text]="true"
                    (onClick)="removeCondition(i)"
                    pTooltip="Remove condition">
                  </p-button>
                </div>
              </div>
            </div>

            <div *ngIf="conditions.length === 0" class="text-center py-4 text-color-secondary">
              No conditions added. Conditions are optional filters for your report.
            </div>
          </p-panel>
        </div>
        
        <!-- Step 4: Charts Configuration -->
        <div *ngIf="builderStep === 4">
          <p-panel header="Chart Configuration" class="mb-4">
            <div class="mb-4">
              <div class="flex align-items-center gap-2 mb-3">
                <p-checkbox 
                  [(ngModel)]="reportForm.controls['enableCharting'].value"
                  [ngModelOptions]="{standalone: true}"
                   (ngModelChange)="onChartingToggle($event)"
                  inputId="enableCharting" 
                  [binary]="true">
                </p-checkbox>
                <label for="enableCharting" class="font-medium">Enable Chart Generation</label>
              </div>
              <small class="text-color-secondary" *ngIf="!canCreateChart()">
                Charts require at least one numeric field and multiple fields selected.
              </small>
            </div>
            
            <div *ngIf="reportForm.controls['enableCharting'].value && canCreateChart()">
              <!-- Chart Type Selection -->
              <div class="mb-4">
                <h4>Chart Type</h4>
                <div class="grid">
                  <div 
                    *ngFor="let chartType of chartTypeOptions" 
                    class="col-12 md:col-4 lg:col-2">
                    <div 
                      class="chart-type-card p-3 border-1  border-round cursor-pointer text-center"
                      [class.border-primary]="chartConfig?.type === chartType.value"
                      [class.bg-primary-50]="chartConfig?.type === chartType.value"
                      (click)="updateChartType(chartType.value)">
                      <i [class]="chartType.icon" class="text-2xl mb-2 block"></i>
                      <span class="text-sm">{{ chartType.label }}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Chart Configuration -->
              <div class="grid mb-4">
                <div class="col-12 md:col-6">
                  <label class="block text-sm font-medium mb-2">Chart Title</label>
                  <input 
                    pInputText 
                    [(ngModel)]="chartConfig!.title"
                    [ngModelOptions]="{standalone: true}"
                    placeholder="Enter chart title" i18n-placeholder
                    class="w-full">
                </div>
                <div class="col-12 md:col-6">
                  <label class="block text-sm font-medium mb-2">Description</label>
                  <input 
                    pInputText 
                    [(ngModel)]="chartConfig!.description"
                    [ngModelOptions]="{standalone: true}"
                    placeholder="Chart description" i18n-placeholder
                    class="w-full">
                </div>
                <div class="col-12 md:col-6">
                  <label class="block text-sm font-medium mb-2">X-Axis Field</label>
                  <p-select 
                    [(ngModel)]="chartConfig!.xAxis"
                    [ngModelOptions]="{standalone: true}"
                    [options]="getAllFields()"
                    optionLabel="label"
                    optionValue="name"
                    placeholder="Select X-axis field" i18n-placeholder
                    class="w-full">
                  </p-select>
                </div>
                <div class="col-12 md:col-6">
                  <label class="block text-sm font-medium mb-2">Y-Axis Fields</label>
                  <p-multiSelect 
                    [(ngModel)]="chartConfig!.yAxis"
                    [ngModelOptions]="{standalone: true}"
                    [options]="getChartableFields()"
                    optionLabel="label"
                    optionValue="name"
                    placeholder="Select Y-axis fields" i18n-placeholder
                    class="w-full">
                  </p-multiSelect>
                </div>
              </div>
              
              <!-- Chart Preview (placeholder) -->
              <div class="chart-preview border-1  border-round p-4 text-center">
                 <div *ngIf="hasValidChartConfig(); else noChartPreview">
                   <i class="pi pi-chart-bar text-4xl text-primary mb-3 block"></i>
                   <p class="text-lg font-medium">{{ chartConfig?.title || 'Chart Preview' }}</p>
                   <p class="text-color-secondary">
                     {{ getChartTypeLabel() }} - 
                     X: {{ getChartXAxisLabel() }}, 
                     Y: {{ getChartYAxisLabels() }}
                   </p>
                </div>
                <ng-template #noChartPreview>
                  <i class="pi pi-image text-4xl text-color-secondary mb-3 block"></i>
                  <p class="text-color-secondary">Configure chart settings to see preview</p>
                </ng-template>
              </div>
            </div>
            
            <div *ngIf="!reportForm.controls['enableCharting'].value" class="text-center py-4 text-color-secondary">
              <i class="pi pi-chart-bar text-4xl mb-3 block"></i>
              <p>Enable charting to create visual representations of your data.</p>
            </div>
          </p-panel>
        </div>
        
        <!-- Step 5: Report Settings -->
        <div *ngIf="builderStep === 5">
          <p-panel header="Report Settings" class="mb-4">
            <div class="grid">
              <div class="col-12 md:col-6">
                <label for="reportName" class="block text-sm font-medium mb-2">
                  Report Name *
                </label>
                <input 
                  id="reportName"
                  pInputText 
                  formControlName="name" 
                  placeholder="Enter report name" i18n-placeholder
                  class="w-full">
              </div>
              <div class="col-12 md:col-6">
                <label for="reportLimit" class="block text-sm font-medium mb-2">
                  Result Limit
                </label>
                <p-inputNumber 
                  id="reportLimit"
                  formControlName="limit" 
                  [min]="1" 
                  [max]="10000"
                  class="w-full">
                </p-inputNumber>
              </div>
              <div class="col-12">
                <label for="reportDescription" class="block text-sm font-medium mb-2">
                  Description
                </label>
                <input 
                  id="reportDescription"
                  pInputText 
                  formControlName="description" 
                  placeholder="Enter report description" i18n-placeholder
                  class="w-full">
              </div>
              <div class="col-12 md:col-6">
                <div class="flex align-items-center gap-2 mt-3">
                  <p-checkbox 
                    formControlName="isPublic" 
                    inputId="isPublic" 
                    [binary]="true">
                  </p-checkbox>
                  <label for="isPublic" class="text-sm">Make this report public</label>
                </div>
              </div>
              <div class="col-12 md:col-6">
                <label for="exportFormats" class="block text-sm font-medium mb-2">
                  Export Formats
                </label>
                <p-multiSelect 
                  id="exportFormats"
                  formControlName="exportFormats" 
                  [options]="exportOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select export formats" i18n-placeholder
                  class="w-full">
                </p-multiSelect>
              </div>
            </div>
          </p-panel>
        </div>

        <!-- Actions -->
         <div class="flex justify-between gap-2 mt-4">
           <div class="flex gap-2">
             <p-button appGlobalLoadingDisable 
               label="Cancel" 
               severity="secondary" 
               (onClick)="closeDialogs()">
             </p-button>
             <p-button appGlobalLoadingDisable 
               label="Test Simple" 
               icon="pi pi-cog" 
               severity="help"
               size="small"
               (onClick)="testSimplifiedPreview()"
               [loading]="loading"
               pTooltip="Test with simplified payload">
             </p-button>
             <p-button appGlobalLoadingDisable 
               label="Preview" 
               icon="pi pi-eye" 
               severity="info"
               (onClick)="previewReport()"
               [loading]="loading">
             </p-button>
           </div>
          <p-button appGlobalLoadingDisable 
            [label]="isEditMode ? 'Update Report' : 'Save Report'" 
            icon="pi pi-save" 
            severity="primary"
            (onClick)="saveReport()"
            [loading]="loading">
          </p-button>
        </div>
      </form>
    </div>
  </p-drawer>

  <!-- Enhanced Execution Results Dialog -->
  <p-dialog 
    [(visible)]="reportExecutionVisible" 
    [header]="'Results: ' + (currentReport?.name || '')" 
    [style]="{width: '95vw', height: '90vh'}"
    [maximizable]="true"
    (onHide)="closeDialogs()">
    
    <div class="execution-content" *ngIf="executionResult">
      <!-- Execution Info & Controls -->
      <div class="flex justify-between align-items-center mb-3">
        <div class="flex gap-4">
          <span class="text-sm">
            <strong>Total Records:</strong> {{ totalRecords | number:'1.0-0':'de-DE' }}
          </span>
          <span class="text-sm">
            <strong>Execution Time:</strong> {{ formatExecutionTime(executionResult.executionTime) }}
          </span>
          <span *ngIf="hasAggregations()" class="text-sm">
            <strong>Grouped Data</strong>
          </span>
        </div>
        <div class="flex gap-2">
          <p-button 
            *ngIf="currentReport?.chartConfig?.enabled && chartData"
            [label]="chartViewVisible ? 'Show Table' : 'Show Chart'"
            [icon]="chartViewVisible ? 'pi pi-table' : 'pi pi-chart-bar'"
            severity="info"
            size="small"
            (onClick)="toggleChartView()">
          </p-button>
          <div class="export-button-group">
            <span class="export-label">Export</span>
            <p-button 
              type="button"
              icon="pi pi-chevron-down"
              styleClass="p-button-icon-only export-dropdown-btn"
              (onClick)="exportMenuResults.toggle($event)"
              aria-label="Export options">
            </p-button>
            <p-menu #exportMenuResults [model]="exportActionItems" [popup]="true"></p-menu>
          </div>
        </div>
      </div>

      <!-- Chart View -->
      <div *ngIf="chartViewVisible && chartData" class="chart-container mb-4">
        <p-chart 
          [type]="chartConfig?.type || 'bar'"
          [data]="chartData"
          [options]="chartConfig?.options"
          width="100%"
          height="400px">
        </p-chart>
      </div>

      <!-- Table View -->
      <div *ngIf="!chartViewVisible">
        <p-table 
          [value]="executionResult.data" 
          [scrollable]="true" 
          scrollHeight="60vh"
          responsiveLayout="scroll">
          
          <ng-template pTemplate="header">
            <tr>
              <!-- Dynamic columns when selectedFields don't match data -->
              <ng-container *ngIf="shouldUseDynamicColumns()">
                <th *ngFor="let fieldName of getDataColumns()">
                  {{ getFieldLabel(fieldName) }}
                </th>
              </ng-container>
              
              <!-- Original columns when selectedFields match data -->
              <ng-container *ngIf="!shouldUseDynamicColumns()">
                <th *ngFor="let fieldName of currentReport?.selectedFields">
                  {{ getFieldLabel(fieldName) }}
                </th>
                <th *ngFor="let calc of currentReport?.calculatedFields || []">
                  {{ calc.label }}
                </th>
                <th *ngFor="let agg of currentReport?.groupBy?.aggregations || []">
                  {{ getAggregatedFieldName(agg) }}
                </th>
              </ng-container>
            </tr>
          </ng-template>
          
          <ng-template pTemplate="body" let-row>
            <tr>
              <!-- Dynamic columns when selectedFields don't match data -->
              <ng-container *ngIf="shouldUseDynamicColumns()">
                <td *ngFor="let fieldName of getDataColumns()">
                  {{ formatValueForDisplay(row[fieldName], fieldName) || '-' }}
                </td>
              </ng-container>
              
              <!-- Original columns when selectedFields match data -->
              <ng-container *ngIf="!shouldUseDynamicColumns()">
                <td *ngFor="let fieldName of currentReport?.selectedFields">
                  {{ formatValueForDisplay(row[fieldName], fieldName) || '-' }}
                </td>
                <td *ngFor="let calc of currentReport?.calculatedFields || []">
                  {{ formatValueForDisplay(row[calc.name], calc.name) || '-' }}
                </td>
                <td *ngFor="let agg of currentReport?.groupBy?.aggregations || []">
                  {{ formatValueForDisplay(row[agg.alias], agg.alias) || '-' }}
                </td>
              </ng-container>
            </tr>
          </ng-template>
          
          <ng-template pTemplate="emptymessage">
            <tr>
              <td [attr.colspan]="getTableColspan()" 
                  class="text-center py-4">
                No data found
              </td>
            </tr>
          </ng-template>
        </p-table>

        <!-- Pagination -->
        <p-paginator 
          *ngIf="totalRecords > pageSize"
          [rows]="pageSize" 
          [totalRecords]="totalRecords"
          [rowsPerPageOptions]="[10, 20, 50, 100]"
          (onPageChange)="onPageChange($event)"
          class="mt-3">
        </p-paginator>
      </div>
    </div>
    
    <ng-template pTemplate="footer">
      <p-button appGlobalLoadingDisable 
        label="Close" 
        severity="secondary" 
        (onClick)="reportExecutionVisible = false">
      </p-button>
    </ng-template>
  </p-dialog>

  <!-- Report Templates Dialog -->
  <p-dialog 
    [(visible)]="templatesVisible" 
    header="Report Templates" 
    [style]="{width: '90vw', height: '80vh'}"
    [maximizable]="true"
    (onHide)="templatesVisible = false">
    
    <div class="templates-content">
      <p class="mb-4 text-color-secondary">
        Choose from comprehensive report templates designed for immediate business value. 
        Templates include fields from all data categories, advanced calculations, grouping, filtering, and visualizations.
        <strong>Try the "Complete System Analytics Dashboard" to see ALL capabilities!</strong>
      </p>
      
      <div *ngFor="let category of templateCategories" class="template-category mb-4">
        <h3 class="category-header p-3 bg-surface-100 border-round mb-3">
          <i class="pi pi-folder-open mr-2"></i>
          {{ category }} Reports
        </h3>
        
        <div class="grid">
          <div 
            *ngFor="let template of getTemplatesByCategory(category)" 
            class="col-12 md:col-6 lg:col-4">
            <div class="template-card p-4 border-1  border-round cursor-pointer hover-lift"
                 (click)="applyReportTemplate(template)">
              <div class="flex justify-between align-items-start mb-3">
                <h4 class="m-0 text-primary">{{ template.name }}</h4>
                <p-tag [value]="template.category" severity="secondary" class="text-xs"></p-tag>
              </div>
              
              <p class="text-sm text-color-secondary mb-3 line-height-3">
                {{ template.description }}
              </p>
              
              <div class="template-features">
                <div class="flex gap-2 mb-2">
                  <p-tag 
                    [value]="template.selectedFields.length + ' fields'" 
                    severity="info" 
                    class="text-xs">
                  </p-tag>
                  <p-tag 
                    *ngIf="template.calculatedFields?.length"
                    [value]="template.calculatedFields.length + ' calculations'" 
                    severity="warning" 
                    class="text-xs">
                  </p-tag>
                  <p-tag 
                    *ngIf="template.groupBy"
                    value="Grouped" 
                    severity="success" 
                    class="text-xs">
                  </p-tag>
                  <p-tag 
                    *ngIf="template.chartConfig?.enabled"
                    value="Charts" 
                    severity="contrast" 
                    class="text-xs">
                  </p-tag>
                </div>
                
                <div class="flex justify-between align-items-center mt-3">
                  <small class="text-color-secondary">
                    Export: {{ template.exportFormats.join(', ').toUpperCase() }}
                  </small>
                  <p-button appGlobalLoadingDisable 
                    label="Use Template" 
                    icon="pi pi-arrow-right" 
                    size="small" 
                    iconPos="right"
                    (onClick)="applyReportTemplate(template); $event.stopPropagation()">
                  </p-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <ng-template pTemplate="footer">
      <p-button appGlobalLoadingDisable 
        label="Close" 
        severity="secondary" 
        (onClick)="templatesVisible = false">
      </p-button>
    </ng-template>
  </p-dialog>

  <!-- Enhanced Preview Dialog -->
  <p-dialog 
    [(visible)]="reportPreviewVisible" 
    header="Report Preview" 
    [style]="{width: '90vw', height: '80vh'}"
    [maximizable]="true"
    (onHide)="closeDialogs()">
    
    <div class="preview-content">
      <p-table 
        [value]="previewData" 
        [scrollable]="true" 
        scrollHeight="60vh"
        responsiveLayout="scroll">
        
        <ng-template pTemplate="header">
          <tr>
            <!-- Dynamic columns when we have preview data -->
            <ng-container *ngIf="shouldShowDynamicPreviewColumns()">
              <th *ngFor="let fieldName of getPreviewDataColumns()">
                {{ getFieldLabel(fieldName) }}
              </th>
            </ng-container>
            
            <!-- Original columns when selectedFields match data -->
            <ng-container *ngIf="shouldShowOriginalPreviewColumns()">
              <th *ngFor="let field of selectedFields">{{ field?.label }}</th>
              <th *ngFor="let calc of calculatedFields">{{ calc?.label }}</th>
            </ng-container>
          </tr>
        </ng-template>
        
        <ng-template pTemplate="body" let-row>
          <tr>
            <!-- Dynamic columns when we have preview data -->
            <ng-container *ngIf="shouldShowDynamicPreviewColumns()">
              <td *ngFor="let fieldName of getPreviewDataColumns()">
                {{ formatValueForDisplay(row[fieldName], fieldName) || '-' }}
              </td>
            </ng-container>
            
            <!-- Original columns when selectedFields match data -->
            <ng-container *ngIf="shouldShowOriginalPreviewColumns()">
              <td *ngFor="let field of selectedFields">
                <ng-container *ngIf="field && field.name">
                  {{ formatValueForDisplay(row[field.name], field.name) || '-' }}
                </ng-container>
              </td>
              <td *ngFor="let calc of calculatedFields">
                <ng-container *ngIf="calc && calc.name">
                  {{ formatValueForDisplay(row[calc.name], calc.name) || '-' }}
                </ng-container>
              </td>
            </ng-container>
          </tr>
        </ng-template>
        
        <ng-template pTemplate="emptymessage">
          <tr>
            <td [attr.colspan]="getPreviewColumnCount()" class="text-center py-4">
              No data available with current configuration
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
    
    <ng-template pTemplate="footer">
      <p-button appGlobalLoadingDisable 
        label="Close" 
        severity="secondary" 
        (onClick)="reportPreviewVisible = false">
      </p-button>
    </ng-template>
  </p-dialog>

  <!-- Loading Spinner -->
  <div *ngIf="loading" class="loading-overlay">
    <p-progressSpinner></p-progressSpinner>
  </div>
</div>

<!-- Toast messages -->
<p-toast></p-toast>

<!-- Confirmation dialog -->
<p-confirmDialog></p-confirmDialog>
